import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowRight, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F4ECDB]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 bg-[#0F1F18]">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D97A3C]">
            Stories & Updates
          </span>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-white mt-2">
            Our Blog
          </h1>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">
            Discover travel tips, nature stories, and updates from Tuskermark Safari Retreat.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 px-6 md:px-12" data-testid="blog-list">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-48 bg-[#E8DDC4] rounded-t-lg"></div>
                  <div className="p-6 bg-white rounded-b-lg">
                    <div className="h-4 bg-[#E8DDC4] rounded w-1/4 mb-3"></div>
                    <div className="h-6 bg-[#E8DDC4] rounded w-3/4 mb-3"></div>
                    <div className="h-4 bg-[#E8DDC4] rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-[#E8DDC4] rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar size={32} className="text-[#6B7A6E]" />
              </div>
              <h3 className="font-serif text-2xl text-[#0F1F18] mb-2">No Blog Posts Yet</h3>
              <p className="text-[#6B7A6E]">Check back soon for updates and stories!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, index) => (
                <Link 
                  key={blog.id} 
                  to={`/blog/${blog.id}`}
                  className={`blog-card group animate-fade-in-up stagger-${(index % 3) + 1}`}
                  style={{ opacity: 0 }}
                  data-testid={`blog-card-${blog.id}`}
                >
                  <div className="h-48 overflow-hidden">
                    {blog.image_url ? (
                      <img 
                        src={blog.image_url} 
                        alt={blog.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#1F4D34] flex items-center justify-center">
                        <span className="font-serif text-3xl text-white/20">{blog.title.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-[#6B7A6E] mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(blog.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {blog.author}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-[#0F1F18] mb-2 group-hover:text-[#1F4D34] transition-colors">
                      {blog.title}
                    </h3>
                    <p className="text-[#6B7A6E] text-sm line-clamp-2 mb-4">
                      {blog.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-[#D97A3C] text-sm font-medium group-hover:gap-3 transition-all">
                      Read More
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
